import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, ButtonAPI, DeckAPI, FilterAPI, MapperAPI, ModalProps, ModalService, PanelAPI, SegmentedControlAPI, Tag, Text, Tooltip, TooltipTrigger } from '@philips-internal/oswald-ui-components';
import { ButtonTheme, ModalButtonType } from '@philips-internal/oswald-ui-constants/lib';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeClear, OnBeforeSubmit, OnChange, OnClick, OnDynamicContentRender, OnMount, OnSelectGroup, OnUnmount, OnUnselectGroup } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, ChangeEvent, ClickEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import moment, { Moment as MomentType } from 'moment';
import React, { ReactElement } from 'react';
import { FilterSuggestionOrders } from '../../../app/types';
import { FragmentNames, PseudonymSuggestionListAliases } from '../../../codegen';
import TokenConstants from '../../../constants/TokenConstants';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { EstablishmenSuggestions, ScheduleItem, SuggestionGroups, SuggestionItems, SuggestionPeriodItem } from '../../../interfaces/schedules-suggestions';
import AppointmentContext from '../../../utils/appointment-context';
import { AppointmentUtils } from '../../../utils/appointment-utils';
import { JWTTokenUtils } from '../../../utils/jwt-token-utils';
import { ParseHelperUtils } from '../../../utils/parse-helper-utils';
import { showToastNotificationInformation } from '../../../utils/toast-notification-utils';
import PseudonymAppointmentsBaseController from '../pseudonym-appointments-base-controller';
import { EstablishmentGroupLocalizationModel } from '../pseudonym-interfaces';
import PseudonymSchedulePriceService, { SchedulePrice } from '../pseudonym-service/pseudonym-schedule-price-service';
import { ScheduleAvailableType, TeleconsultationType, YES } from '../pseudonym-types';
import PseudonymSuggestionListModel from './pseudonym-suggestion-list-model';

interface FilterSuggestionRecord {
  establishment?: EstablishmentGroupLocalizationModel;
  onlyTeleconsultation?: boolean;
  ordenation?: FilterSuggestionOrders;
}

interface DesiredAppointmentDate {
  date: MomentType;
}

const FilterSuggestionType = {
  AMOUNT: 'AMOUNT',
  DURATION: 'DURATION',
  TIME: 'TIME'
}

const FilterSuggestionOrders = new Map<string, FilterSuggestionOrders>([
  [FilterSuggestionType.AMOUNT, {
    description: 'tws.label-amount',
    id: FilterSuggestionType.AMOUNT,
  }],
  [FilterSuggestionType.DURATION, {
    description: 'tws.label-duration',
    id: FilterSuggestionType.DURATION,
  }],
  [FilterSuggestionType.TIME, {
    description: 'tws.label-time',
    id: FilterSuggestionType.TIME,
  }],
]);

@Controller({ component: PseudonymSuggestionListAliases.LAYOUT_SUGGESTION_LIST, fragment: FragmentNames.PSEUDONYM_SUGGESTION_LIST })
export default class PseudonymSuggestionListController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymSuggestionListAliases.PANEL_SUGGESTIONS)
    panelSuggestionsApi!: PanelAPI;

  @Inject(PseudonymSuggestionListAliases.SEGMENTED_CONTROL_WEEK_DAYS)
    segmentedControlWeekDaysApi!: SegmentedControlAPI;

  @Inject(PseudonymSuggestionListAliases.PANEL_SUGGESTION_GROUPS)
    panelSuggestionGroupsApi!: PanelAPI;

  @Inject(PseudonymSuggestionListAliases.MAPPER_SUGGESTION_GROUPS)
    mapperSuggestionGroupsApi!: MapperAPI;

  @Inject(PseudonymSuggestionListAliases.PANEL_LOAD_CONTENT)
    panelLoadContentApi!: PanelAPI;

  @Inject(PseudonymSuggestionListAliases.BUTTON_CONFIRM)
    buttonConfirmApi!: ButtonAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('PseudonymSchedulePriceService')
    pseudonymSchedulePriceService!: PseudonymSchedulePriceService;

  private suggestionListModel!: PseudonymSuggestionListModel;

  @OnMount({ target: PseudonymSuggestionListAliases.PANEL_SUGGESTIONS })
  handleOnMountPanelSuggestions(event: MountEvent): void {
    if (!event) {
      return;
    }

    const suggestionGroup = this.context.get('data') as SuggestionGroups;
    const establishmentSuggestions = suggestionGroup.establishmenSuggestionsGroups;
    const isShowPrice = suggestionGroup.isShowPrice;
    const numberTimesSchedulingDay = suggestionGroup.numberTimesSchedulingDay;
    this.suggestionListModel = new PseudonymSuggestionListModel(establishmentSuggestions, isShowPrice, numberTimesSchedulingDay, suggestionGroup);
    this.processWeekdays();
  }

  private processWeekdays(): void {
    const dateFilterItens: Array<Record<string, Value>> = [];
    const establishmentSuggestions = this.suggestionListModel.establishmentSuggestions;

    establishmentSuggestions.forEach((suggestion) => {
      const date = dateFilterItens.find(item => item.date === suggestion.value);

      if (!date) {
        dateFilterItens.push({
          date: suggestion.value,
          id: suggestion.value,
          value: moment(suggestion.value).format('dddd, DD/MM/YYYY')
        });
      }
    });

    const dateFilterItensSorted = dateFilterItens.slice().sort((dateA, dateB) => {
      const dateAParsed = moment(dateA.date as string);
      const dateBParsed = moment(dateB.date as string);
      return dateAParsed.diff(dateBParsed);
    });

    const dateFilterItensFirstValue = [dateFilterItensSorted[0]];
    const lastDataFilterSelected = this.segmentedControlWeekDaysApi.getValue() ? this.segmentedControlWeekDaysApi.getValue() : dateFilterItensFirstValue;
    const dateFilterSelected = dateFilterItensSorted.length === 1 ? dateFilterItensSorted : lastDataFilterSelected;
    this.segmentedControlWeekDaysApi.setData(dateFilterItensSorted);
    this.segmentedControlWeekDaysApi.setValue(dateFilterSelected);

    if (dateFilterItensSorted.length === 0) {
      this.panelSuggestionGroupsApi.setVisible(true);
    }
  }

  @OnMount({ target: PseudonymSuggestionListAliases.AUTOCOMPLETE_ESTABLISHMENT })
  handleOnMountAutocompleteEstablishment(event: MountEvent): void {
    const autocomplete = event.target as AutocompleteAPI;
    const suggestionGroup = this.suggestionListModel.suggestionGroup;
    const establishmentsSuggestions = suggestionGroup.establishmenSuggestionsGroups;
    const establishments: Array<EstablishmentGroupLocalizationModel> = [];

    establishmentsSuggestions.forEach(establishment => {
      const hasItem = establishments.find(item => item.nrSequencia === establishment.cdEstabelecimento);
      if (!hasItem) {
        establishments.push({
          dsGrupoEstab: establishment.dsEstablishment,
          nrSequencia: establishment.cdEstabelecimento,
        });
      }
    });

    autocomplete.setData(establishments as unknown as Array<Record<string, Value>>);
  }

  @OnMount({ target: PseudonymSuggestionListAliases.AUTOCOMPLETE_SORT })
  handleOnMountAutocompleteSort(event: MountEvent): void {
    const autocomplete = event.target as AutocompleteAPI;
    const filterOrders = FilterSuggestionOrders;
    const isShowPrice = this.suggestionListModel.isShowPrice;
    const isShowDuration = JWTTokenUtils.getClaimValue(TokenConstants.SHOW_SCHEDULING_DURATION) === YES;

    if (!isShowDuration) {
      filterOrders.delete(FilterSuggestionType.DURATION);
    }
    if (!isShowPrice) {
      filterOrders.delete(FilterSuggestionType.AMOUNT);
    }

    autocomplete.setData([...filterOrders.values()] as unknown as Array<Record<string, Value>>);
  }

  @OnChange({ target: PseudonymSuggestionListAliases.SEGMENTED_CONTROL_WEEK_DAYS })
  handleOnChangeSegmentedControlWeekDays(event: ChangeEvent<Value>): void {
    if (!event?.value) {
      return;
    }

    const segmentedDateItem = event.value as Array<SuggestionPeriodItem>;
    if (!segmentedDateItem?.length) {
      return;
    }

    this.suggestionListModel.hasSelected = false;
    this.panelSuggestionGroupsApi.setVisible(true);
    setTimeout(() => {
      const segmentedDate = segmentedDateItem[0].date;
      const establishmentSuggestions = this.suggestionListModel.establishmentSuggestions
        .filter(suggestion => suggestion.value === segmentedDate)
        .slice(0, this.suggestionListModel.numberTimesSchedulingDay);

      establishmentSuggestions.forEach((establishmentSuggestion, index) => {
        establishmentSuggestion.suggestions.forEach(suggestionItems => {
          suggestionItems.suggestionItems.forEach(suggestion => {
            suggestion.suggestionGroupId = index + 1;
            suggestion.dsEstablishment = establishmentSuggestion.dsEstablishment;
            suggestion.cdEstabelecimento = establishmentSuggestion.cdEstabelecimento;
            suggestion.isShowPrice = this.suggestionListModel.isShowPrice;
          });
        });
      });

      this.mapperSuggestionGroupsApi.setData(establishmentSuggestions as unknown as Array<Record<string, Value>>);
    });
  }

  @OnMount(PseudonymSuggestionListAliases.PANEL_SUGGESTION_GROUPS)
  handleOnMountPanelSugeestionGroups(): void {
    this.panelSuggestionGroupsApi.setVisible(false);
  }

  @OnMount(PseudonymSuggestionListAliases.MAPPER_SUGGESTION_GROUPS)
  handleOnMountMapperSuggestionGroups(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    mapperApi.setData([]);
  }

  @OnMount({ target: PseudonymSuggestionListAliases.DECK_SUGGESTION_GROUPS })
  handleOnMountDeckSuggestionGroups(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    deck.setDone(true);
  }

  @OnMount({ target: PseudonymSuggestionListAliases.DECK_SUGGESTION_ITEMS })
  handleOnMountDeckSuggestionItems(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    const suggestionItems = deck.getData() as unknown as Array<SuggestionItems>;
    const identifier = suggestionItems[0]?.suggestionItems[0]?.suggestionGroupId;

    deck.setDone(true);
    if (identifier) {
      const decksSuggestionItems = [] as Array<DeckAPI>;
      decksSuggestionItems[identifier] = deck;
      this.suggestionListModel.decksSuggestionItems = decksSuggestionItems;
    }
  }

  @OnUnmount({ target: PseudonymSuggestionListAliases.DECK_SUGGESTION_ITEMS })
  handleUnMountDeckSuggestionItems(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    if (deck?.selectGroup?.length > 0) {
      this.suggestionListModel.hasSelected = false;
      this.suggestionListModel.lastDeckApi = undefined;
      this.suggestionListModel.suggestionItemsSelected = [] as Array<SuggestionItems>;
      this.buttonConfirmApi.setEnabled(false);
    }
  }

  @OnDynamicContentRender({ target: PseudonymSuggestionListAliases.DECK_SUGGESTION_ITEMS })
  handleOnDynamicContentRenderDeckSuggestionItems(event: RenderEvent): void {
    if (!event) {
      return;
    }

    const render = event.render;
    const currentDeckAPI = event.target as DeckAPI;
    const suggestionsItems = event.data as Array<SuggestionItems>;
    const schedulePrice = { currencySymbol: '', total: 0 } as SchedulePrice;
    let canSelected = true;

    suggestionsItems.forEach(suggestionsItem => {
      suggestionsItem.suggestionItems.forEach(item => {
        if (item.availableType === ScheduleAvailableType.READ_ONLY) {
          canSelected = false;
        }
        if (!item.price) {
          item.price = '0';
          item.priceNoFormatted = 0;
        }

        this.pseudonymSchedulePriceService.calculateSchedulePrice(item, schedulePrice);
      });
    });

    const renderSuggestionDate = this.generateSuggestionItemsReactElement(currentDeckAPI, suggestionsItems, schedulePrice, canSelected);
    render(renderSuggestionDate);
  }

  private generateSuggestionItemsReactElement(
    currentDeckAPI: DeckAPI,
    suggestionsItems: Array<SuggestionItems>,
    schedulePrice: SchedulePrice,
    canSelected: boolean
  ): () => ReactElement {
    const suggestionItem = suggestionsItems[0];
    const isShowTotal = AppointmentUtils.isShowTotal(suggestionItem?.suggestionItems || []);
    const totalFormatted = this.pseudonymSchedulePriceService.formatTotal(schedulePrice);
    const totalLabel = `${MessageService.translate('tws.appointment-suggestion.price')}: ${ParseHelperUtils.parseToTextHtml(totalFormatted)}`;
    const viewOnlyClass = `${!canSelected ? 'deck-title-suggestion-item--no-select' : 'deck-title-suggestion-item'}`;
    const oswaldDeckHeaderClass = 'c-deck__group-header-default-content';
    const oswaldDeckTitleClass = 'c-deck__group-header-title';
    const oswaldDeckTitleText = `${MessageService.translate('tws.appointment-suggestion.suggestion')} ${suggestionItem.suggestionId}`;

    return canSelected
      ? this.generateSuggestionDateCanSelectedReactElement(currentDeckAPI, isShowTotal, totalLabel,oswaldDeckHeaderClass, oswaldDeckTitleClass, oswaldDeckTitleText)
      : this.generateSuggestionDateReactElement(isShowTotal, totalLabel, oswaldDeckHeaderClass, viewOnlyClass, oswaldDeckTitleClass, oswaldDeckTitleText);
  }

  private generateSuggestionDateCanSelectedReactElement(
    currentDeckAPI: DeckAPI, isShowTotal: boolean, totalLabel: string,
    oswaldDeckHeaderClass: string, oswaldDeckTitleClass: string, oswaldDeckTitleText: string
  ): () => ReactElement {
    const renderSuggestionDateCanSelected: () => ReactElement = () => {
      return (
        <>
          <div className="wrapper-full-click" onClick={currentDeckAPI.onClick.fire}></div>
          <div className={`${oswaldDeckHeaderClass} deck-title-suggestion-item`}>
            <div className={oswaldDeckTitleClass}>
              <Text value={oswaldDeckTitleText} />
            </div>
            {this.suggestionListModel.isShowPrice && isShowTotal &&
              <div className='price'>
                <Text value={totalLabel} />
              </div>}
          </div>
        </>
      );
    }
    return renderSuggestionDateCanSelected;
  }

  private generateSuggestionDateReactElement(
    isShowTotal: boolean, totalLabel: string,
    oswaldDeckHeaderClass: string, viewOnlyClass: string, oswaldDeckTitleClass: string, oswaldDeckTitleText: string
  ): () => ReactElement {
    const renderSuggestionDate: () => ReactElement = () => {
      return (
        <div className={`${oswaldDeckHeaderClass} ${viewOnlyClass}`}>
          <div className={oswaldDeckTitleClass}>
            <Text value={oswaldDeckTitleText} />
          </div>
          <Tooltip
            triggers={[TooltipTrigger.HOVER]}
            value={MessageService.translate('patient.tooltip.visualisation')}
            multiline={true}>
            <div className='tooltip-custom__target'>
              <Tag
                value={MessageService.translate('patient.view-only')}
                formatters={{}}
              />
            </div>
          </Tooltip>
          {this.suggestionListModel.isShowPrice && isShowTotal &&
            <div className='price'>
              <Text value={totalLabel} />
            </div>
          }
        </div>
      );
    }
    return renderSuggestionDate;
  }

  @OnSelectGroup({ target: PseudonymSuggestionListAliases.DECK_SUGGESTION_ITEMS })
  handleOnSelectGroupDeckSuggestionItems(event: SelectItemEvent<Record<string, Value>[]>): void {
    if (!event) {
      return;
    }

    const deckApi = event.target as unknown as DeckAPI;
    const selecetedItems = event.item as unknown as Array<SuggestionItems>;
    const lastDeckApi = this.suggestionListModel.lastDeckApi;
    let hasSelected = this.suggestionListModel.hasSelected;

    if (this.checkCanSelectByType(selecetedItems)) {
      const { suggestionId: identifierGroup, suggestionGroupId: deckIndex } = selecetedItems[0].suggestionItems[0];
      const suggestionsHybrid = selecetedItems[0].suggestionItems
        .filter(suggestion => suggestion.teleconsultationType === ScheduleAvailableType.HIBRID);

      if (suggestionsHybrid?.length) {
        this.openDialogHasConfirmTypeConsultation()
      }

      if (lastDeckApi && hasSelected) {
        lastDeckApi.unselectAll();
        deckApi.selectGroup(String(identifierGroup));
      } else {
        hasSelected = true;
      }

      const decksSuggestionItems = this.suggestionListModel.decksSuggestionItems;
      this.suggestionListModel.lastDeckApi = decksSuggestionItems[deckIndex]

      const hasItemSelected = hasSelected && selecetedItems?.length > 0;
      if (hasItemSelected) {
        this.suggestionListModel.suggestionItemsSelected = selecetedItems;
      }

      this.buttonConfirmApi.setEnabled(hasItemSelected);
    } else {
      hasSelected = false;
      deckApi.unselectAll();

      const suggestionItemsSelected = this.suggestionListModel.suggestionItemsSelected;
      if (lastDeckApi && suggestionItemsSelected?.length > 0) {
        const lastDeckSelected = suggestionItemsSelected[0].suggestionId;
        lastDeckApi.selectGroup(String(lastDeckSelected || ''));
        hasSelected = Boolean(lastDeckSelected);
      }
    }

    this.suggestionListModel.hasSelected = hasSelected;
  }

  private checkCanSelectByType(selecetedItems: Array<SuggestionItems>): boolean {
    const suggestionItems = selecetedItems[0].suggestionItems;
    const canSelect = suggestionItems.filter(suggestion => suggestion.availableType === ScheduleAvailableType.READ_ONLY).length === 0;
    return canSelect;
  }

  private openDialogHasConfirmTypeConsultation(): void {
    const suggestionItemsSelected = this.suggestionListModel.suggestionItemsSelected;
    ModalService.openDefault({
      closeable: true,
      dockButtons: [
        {
          action: () => { },
          theme: ButtonTheme.SECONDARY,
          title: 'patient.cancel',
          type: ModalButtonType.CANCEL,
        },
        {
          action: () => { },
          title: 'patient.burron.confirm',
          type: ModalButtonType.SUCCESS,
        }
      ],
      fragment: FragmentNames.PSEUDONYM_SUGGESTION_TYPE_CONSULTATION,
      onSubmit: (event) => {
        suggestionItemsSelected[0].suggestionItems.forEach(suggestion => {
          if (suggestion.teleconsultationType === ScheduleAvailableType.HIBRID) {
            suggestion.suggestionTypeConsultation = event.record.suggestionTypeConsultation.value;
          }
        });
      },
      title: 'text.choose-an-option-below',
    });
  }

  @OnUnselectGroup({ target: PseudonymSuggestionListAliases.DECK_SUGGESTION_ITEMS })
  handleOnUnselectGroupDeckSuggestionItems(event: SelectItemEvent<Record<string, Value>[]>): void {
    if (!event) {
      return;
    }

    this.suggestionListModel.hasSelected = false;
    this.suggestionListModel.lastDeckApi = undefined;
    this.buttonConfirmApi.setEnabled(false);
  }

  @OnMount({ target: PseudonymSuggestionListAliases.FILTER_SUGGESTION_TIMES })
  handleOnMountFilterSuggestionTimes(event: MountEvent): void {
    const filter = event.target as FilterAPI;
    filter.setData({ ordenation: FilterSuggestionOrders.get(FilterSuggestionType.TIME) });
  }

  @OnBeforeClear({ target: PseudonymSuggestionListAliases.FILTER_SUGGESTION_TIMES })
  handleOnBeforeClearFilterSuggestionTimes(event: AbortableSubmitEvent): void {
    const filter = event.target as FilterAPI;
    filter.clear();
    filter.setData({});
  }

  @OnBeforeSubmit({ target: PseudonymSuggestionListAliases.FILTER_SUGGESTION_TIMES })
  handleOnBeforeSubmitFilterSuggestionsTimes(event: AbortableSubmitEvent): void {
    const filterSuggestionRecord = event.record as FilterSuggestionRecord;
    const { establishment, ordenation, onlyTeleconsultation } = filterSuggestionRecord;
    let establishmentSuggestions = this.suggestionListModel.establishmentSuggestionsDefault;

    this.panelSuggestionsApi.setVisible(false);
    this.panelLoadContentApi.setVisible(true);
    try {
      if (establishment) {
        establishmentSuggestions = establishmentSuggestions.filter(establishmentSuggestion => establishmentSuggestion.cdEstabelecimento === establishment.nrSequencia);
        if (!ordenation) {
          establishmentSuggestions = this.getSuggestionsOrdered(establishmentSuggestions, FilterSuggestionType.TIME);
        }
      }

      if (onlyTeleconsultation) {
        establishmentSuggestions = this.doTeleconsultationFilter(establishmentSuggestions, onlyTeleconsultation);
      }

      if (ordenation) {
        establishmentSuggestions = this.getSuggestionsOrdered(establishmentSuggestions, ordenation.id);
      }
    } catch {
      this.showToastNotificationLoadDataError();
    } finally {
      this.panelSuggestionsApi.setVisible(true);
      this.panelLoadContentApi.setVisible(false);

      this.suggestionListModel.establishmentSuggestions = establishmentSuggestions;
      this.processWeekdays();
    }
  }

  private getSuggestionsOrdered(suggestions: Array<EstablishmenSuggestions>, ordenationId: string): Array<EstablishmenSuggestions> {
    switch (ordenationId) {
      case FilterSuggestionType.AMOUNT: {
        return suggestions.sort((first, second) => {
          const resultComparePrices = this.compareSuggestionsPrices(first, second);
          const resultCompareStartDates = this.compareSuggestionsStartDates(first, second);
          return resultComparePrices === 0 ? resultCompareStartDates : resultComparePrices;
        });
      }
      case FilterSuggestionType.DURATION: {
        return suggestions.sort((first, second) => {
          const resultCompareSortingLogicOfTypeDuration = this.compareSortingLogicOfTypeDuration(first, second);
          const resultCompareStartDates = this.compareSuggestionsStartDates(first, second);
          return resultCompareSortingLogicOfTypeDuration === 0 ? resultCompareStartDates : resultCompareSortingLogicOfTypeDuration;
        });
      }
      case FilterSuggestionType.TIME: {
        return suggestions.sort((first, second) => {
          const resultCompareStartDates = this.compareSuggestionsStartDates(first, second);
          const resultCompareSortingLogicOfTypeDuration = this.compareSortingLogicOfTypeDuration(first, second);
          return resultCompareStartDates === 0 ? resultCompareSortingLogicOfTypeDuration : resultCompareStartDates;
        });
      }
      default: {
        return [];
      }
    }
  }

  private compareSuggestionsPrices(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstPriceValues = this.getSuggestionsPrices(first.suggestions);
    const secondPriceValues = this.getSuggestionsPrices(second.suggestions);
    const firstMinPriceValue = Math.min(...firstPriceValues);
    const secondMinPriceValue = Math.min(...secondPriceValues);
    return this.filterSuggestionComparator(firstMinPriceValue, secondMinPriceValue);
  }

  private getSuggestionsPrices(suggestions: Array<SuggestionItems>): Array<number> {
    return suggestions.reduce<Array<number>>((result, value) => {
      const suggestItemsAmounts = value.suggestionItems.map(item => item.priceNoFormatted);
      result.push(Math.min(...suggestItemsAmounts));
      return result;
    }, []);
  }

  private compareSuggestionsStartDates(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstStartDateTimes = this.getSuggestionStartDates(first);
    const secondStartDateTimes = this.getSuggestionStartDates(second);
    const firstMinStartDate = Math.min(...firstStartDateTimes);
    const secondMinStartDate = Math.min(...secondStartDateTimes);
    return this.filterSuggestionComparator(firstMinStartDate, secondMinStartDate);
  }

  private getSuggestionStartDates(establishmentSuggestion: EstablishmenSuggestions): Array<number> {
    return establishmentSuggestion.events.map(value => moment.utc(value.startDate).unix());
  }

  private compareSortingLogicOfTypeDuration(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstDuration = this.extractNumberFromString(first.filterParameter);
    const secondDuration = this.extractNumberFromString(second.filterParameter);
    return this.filterSuggestionComparator(firstDuration, secondDuration);
  }

  private extractNumberFromString(value?: string): number {
    return Number(value?.replace(/[^0-9]/g, '') ?? 0);
  }

  private filterSuggestionComparator(firstItem: number, secondItem: number): number {
    if (firstItem < secondItem) { return -1; }
    if (firstItem > secondItem) { return 1; }
    return 0;
  }

  private doTeleconsultationFilter(suggestionList: Array<EstablishmenSuggestions>, onlyTeleconsultation: boolean): Array<EstablishmenSuggestions> {
    const newSuggestionsCard = suggestionList
      .map(suggestionCard => {
        const suggestionItems = suggestionCard.suggestions;
        const suggestionItemsFiltered = this.doFilterSuggestionItems(suggestionItems, onlyTeleconsultation);

        return suggestionItemsFiltered.length > 0
          ? { ...suggestionCard, suggestions: suggestionItemsFiltered }
          : null;
      })
      .filter(value => value != null) as Array<EstablishmenSuggestions>;

    return newSuggestionsCard;
  }

  private doFilterSuggestionItems(suggestionItems: Array<SuggestionItems>, onlyTeleconsultation: boolean): Array<SuggestionItems> {
    const newSuggestionItems: Array<SuggestionItems> = [];
    suggestionItems.forEach(suggestion => {
      const suggestionFiltered = suggestion.suggestionItems.filter(item => {
        return item.teleconsultationType === TeleconsultationType.TELECONSULTATION ||
          item.teleconsultationType === ScheduleAvailableType.HIBRID && onlyTeleconsultation
      });

      if (suggestionFiltered.length > 0) {
        newSuggestionItems.push(suggestion);
      }
    });

    return newSuggestionItems;
  }

  @OnClick({ target: PseudonymSuggestionListAliases.BUTTON_CONFIRM })
  handleOnClickButtonConfirm(event: ClickEvent): void {
    if (!event || !this.suggestionListModel.hasSelected) {
      return;
    }

    const allowSelectDesiredDate = this.context.get('allowSelectDesiredDate') as boolean;
    allowSelectDesiredDate ? this.openAskForDesiredDateDialog(event) : this.handleConfirmSuggestion(event);
  }

  private openAskForDesiredDateDialog(clickEvent: ClickEvent): void {
    const desiredDateCustomMessage = this.context.get('desiredDateCustomMessage') as string;
    ModalService.openDialog({
      closeable: false,
      dialogDescription: desiredDateCustomMessage || 'patient.appoitment.correct.date',
      dockButtons: [
        {
          action: () => this.openModalToSelectDesiredAppointmentDate(clickEvent),
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: () => this.handleAfterSubmitSuggestion(clickEvent),
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ],
      title: 'patient.appointment.confirm.title'
    });
  }

  private openModalToSelectDesiredAppointmentDate(clickEvent: ClickEvent): void {
    const data = this.context.get('data') as Record<string, Array<Value>>;
    const modalProps: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: FragmentNames.PSEUDONYM_SUGGESTION_CHOOSE_ANOTHER_DATE,
      noPadding: true,
      onSubmit: evt => this.handleSubmitDesiredDate(evt, clickEvent, data),
      title: '',
      uuid: '',
    }

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps);
  }

  private handleSubmitDesiredDate(submitEvent: SubmitEvent, clickEvent: ClickEvent, data: Record<string, Array<Value>>): void {
    const desiredAppointmentDate = submitEvent.record as DesiredAppointmentDate;
    const desiredDate = moment(desiredAppointmentDate.date).utc().format('YYYY-MM-DD');
    const selecetedItems = this.suggestionListModel.suggestionItemsSelected;

    if (this.suggestionListModel.hasSelected && selecetedItems?.length > 0) {
      selecetedItems.forEach(item => {
        item.suggestionItems.forEach(suggestionItem => {
          suggestionItem.date = desiredDate;
          const body = {
            desiredDate: desiredDate,
            scheduleType: suggestionItem.appointmentType,
            sequenceIntegratedSchedule: data.nrSeqAgeint,
            sequenceNumber: suggestionItem.nrSeqAgeintItem
          };

          HttpService.post(APPLICATION_ENDPOINTS.DESIRED_DATE, {}, { body });
        });
      });
    }

    this.handleAfterSubmitSuggestion(clickEvent);
  }

  private handleAfterSubmitSuggestion(clickEvent: ClickEvent): void {
    this.handleConfirmSuggestion(clickEvent)
      .then(() => showToastNotificationInformation(MessageService.translate('patient.desired.date.acknowledgment')));
  }

  private handleConfirmSuggestion(clickEvent: ClickEvent): Promise<void> {
    if (!clickEvent) {
      return Promise.reject();
    }

    const selecetedItems = this.suggestionListModel.suggestionItemsSelected;
    let hasGroupingWithRequest = false;

    const suggestionItems = selecetedItems[0].suggestionItems;
    if (this.suggestionListModel.hasSelected && selecetedItems[0]) {
      if (suggestionItems?.length > 1) {
        const suggestionItem = suggestionItems.find(item => item.availableType === ScheduleAvailableType.REQUEST);
        hasGroupingWithRequest = Boolean(suggestionItem);
      }

      return this.checkNecessaryConfirmationItems(selecetedItems, hasGroupingWithRequest)
        .then(() => this.submittableFragmentContext.submit(new SubmitEvent({ record: this.suggestionListModel.selectedItemList })))
        .catch(this.openDialogHasAppointment);
    }

    return Promise.resolve();
  }

  private checkNecessaryConfirmationItems(selecetedItems: Array<SuggestionItems>, hasGroupingWithRequest: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const suggestionItems = selecetedItems[0].suggestionItems;
      const confirmWantRequestPromises: Array<Promise<void>> = [];
      const selectedItemList = this.suggestionListModel.selectedItemList;

      suggestionItems.forEach(suggestion => {
        if (suggestion.availableType === ScheduleAvailableType.REQUEST && !this.suggestionListModel.wantToRequest) {
          const promise = this.confirmWantToRequest(suggestion)
            .catch(() => reject());

          confirmWantRequestPromises.push(promise);
        } else {
          selectedItemList.push(suggestion);
        }
      });

      Promise.all(confirmWantRequestPromises)
        .then(() => {
          if (!hasGroupingWithRequest) {
            const suggestionGroup = this.suggestionListModel.suggestionGroup;
            const selectedSchedules = selectedItemList.filter(item => item.availableType !== ScheduleAvailableType.REQUEST);
            AppointmentContext.callUpdateAppointmentCard(suggestionGroup.groupId, selectedSchedules);
          }

          this.suggestionListModel.selectedItemList = selectedItemList;
          resolve();
        })
        .catch(() => reject());
    });
  }

  private confirmWantToRequest(suggestion: ScheduleItem): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const modalId = 'b86ad408-df95-44db-a786-eccff7b2053d';
      ModalService.close(modalId);
      ModalService.openDialog({
        closeable: false,
        dialogDescription: MessageService.translate('patient.need-to-confirm'),
        dockButtons: [
          {
            action: () => {
              this.suggestionListModel.wantToRequest = false;
              reject();
            },
            title: 'patient.cancel',
            type: ModalButtonType.CANCEL,
          },
          {
            action: () => {
              this.availableTypeModal(suggestion)
                .then(() => {
                  const suggestionGroup = this.suggestionListModel.suggestionGroup;
                  const selectedItemList = this.suggestionListModel.selectedItemList;
                  AppointmentContext.callUpdateAppointmentCard(suggestionGroup.groupId, selectedItemList);
                  this.suggestionListModel.wantToRequest = true;
                  resolve();
                });
            },
            title: 'patient.confirm',
            type: ModalButtonType.SUCCESS,
          }
        ],
        id: modalId,
        title: 'tws.appointment-suggestion.suggestion',
      });
    });
  }

  private availableTypeModal(suggestion: ScheduleItem): Promise<void> {
    return new Promise<void>((resolve) => {
      ModalService.openDefault({
        closeable: false,
        contextData: { data: suggestion as unknown as Record<string, string> },
        dockButtons: [],
        fragment: FragmentNames.PSEUDONYM_SUGGESTION_CHECK_AVAILABLE,
        onSubmit: (event) => {
          const selectedPeriod = event.record as SuggestionPeriodItem;
          suggestion.observation = selectedPeriod.observation;
          suggestion.period = selectedPeriod.period;
          suggestion.attachedPrescheduleFiles = selectedPeriod.attachedPrescheduleFiles;

          const selectedItemList = this.suggestionListModel.selectedItemList;
          selectedItemList.push(suggestion);
          this.suggestionListModel.selectedItemList = selectedItemList;

          resolve();
        },
        title: '',
      });
    });
  }

  private openDialogHasAppointment(text: string): void {
    ModalService.openDialog({
      closeable: true,
      dialogDescription: text,
      dockButtons: [
        {
          action: () => { return },
          title: 'patient.confirm',
          type: ModalButtonType.SUCCESS,
        },
      ],
      title: 'patient.process.aborted',
    });
  }

}
